import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Church, Pencil, Trash2, MoreHorizontal, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import TableFilterBar from "@/components/table/TableFilterBar";
import { cn } from "@/lib/utils";
import { useChurch } from "@/hooks/useChurch";
import { usePermissions } from "@/hooks/usePermissions";
import { useFreemium } from "@/hooks/useFreemium";
import { supabase } from "@/integrations/supabase/client";

type Ministry = {
  id: string;
  name: string;
  description: string | null;
  type: string;
  level: string;
  parent_id: string | null;
  church_id: string;
  created_at: string;
};

export default function OrganizationsPage() {
  const { profile } = useChurch();
  const navigate = useNavigate();
  const { can, isChurchAdmin } = usePermissions();
  const { isPreview, requireSubscription } = useFreemium();
  const queryClient = useQueryClient();

  const isAdmin = isChurchAdmin || can("can_manage_organizations");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"cards" | "list">("list");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMinistry, setEditingMinistry] = useState<Ministry | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [deletingMinistry, setDeletingMinistry] = useState<Ministry | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const hasAttemptedGeneralSeedRef = useRef(false);

  const isBuiltInGeneral = (ministryName: string) => ministryName.trim().toLowerCase() === "general";

  const { data: ministries = [], isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("type", "ministry")
        .eq("level", "organization")
        .order("name", { ascending: true });

      if (error) throw error;
      return (data || []) as Ministry[];
    },
  });

  const filteredMinistries = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = q
      ? ministries.filter((m) => m.name.toLowerCase().includes(q) || (m.description || "").toLowerCase().includes(q))
      : ministries;
    return [...filtered].sort((a, b) => {
      const aGen = a.name.trim().toLowerCase() === "general" ? 0 : 1;
      const bGen = b.name.trim().toLowerCase() === "general" ? 0 : 1;
      return aGen - bGen;
    });
  }, [ministries, search]);

  const hasBuiltInGeneral = useMemo(
    () => ministries.some((ministry) => isBuiltInGeneral(ministry.name)),
    [ministries],
  );

  const ensureGeneralMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) return;

      const { data: existingGeneral, error: existingGeneralError } = await supabase
        .from("organizations")
        .select("id")
        .eq("church_id", profile.id)
        .eq("type", "ministry")
        .eq("level", "organization")
        .ilike("name", "general")
        .limit(1)
        .maybeSingle();

      if (existingGeneralError) throw existingGeneralError;
      if (existingGeneral) return;

      const { error: insertError } = await supabase.from("organizations").insert({
        name: "General",
        description: "Built-in default ministry",
        type: "ministry",
        level: "organization",
        parent_id: null,
        church_id: profile.id,
      });

      if (insertError && !insertError.message.toLowerCase().includes("duplicate")) {
        throw insertError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organizations_full"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_organizations"] });
    },
    onError: (error: Error) => {
      console.error("Failed to ensure built-in General ministry", error);
    },
  });

  useEffect(() => {
    if (!profile?.id || isLoading || hasAttemptedGeneralSeedRef.current) return;
    hasAttemptedGeneralSeedRef.current = true;
    if (!hasBuiltInGeneral) ensureGeneralMutation.mutate();
  }, [profile?.id, isLoading, hasBuiltInGeneral]);

  const addMinistryMutation = useMutation({
    mutationFn: async ({ name, description }: { name: string; description: string }) => {
      const { error } = await supabase.from("organizations").insert({
        name: name.trim(),
        description: description.trim() || null,
        type: "ministry",
        level: "organization",
        parent_id: null,
        church_id: profile?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organizations_full"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_organizations"] });
      
      closeDialog();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMinistryMutation = useMutation({
    mutationFn: async ({ id, name, description }: { id: string; name: string; description: string }) => {
      const { error } = await supabase
        .from("organizations")
        .update({ name: name.trim(), description: description.trim() || null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organizations_full"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_organizations"] });
      
      closeDialog();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMinistryMutation = useMutation({
    mutationFn: async (id: string) => {
      // Find the org name before deleting (needed to clean text references)
      const org = ministries.find((m) => m.id === id);
      const orgName = org?.name ?? "";

      // Clean ministry references from members.department (comma-separated ministries)
      const { data: memberRows } = await supabase
        .from("members")
        .select("id, department")
        .eq("church_id", profile!.id)
        .not("department", "is", null);

      if (memberRows?.length && orgName) {
        const targetName = orgName.trim().toLowerCase();

        const updates = memberRows
          .map((row) => {
            const current = String((row as any).department || "");
            const parts = current
              .split(",")
              .map((value: string) => value.trim())
              .filter(Boolean);

            const nextParts = parts.filter((value: string) => value.toLowerCase() !== targetName);
            if (nextParts.length === parts.length) return null;

            // Keep a valid default ministry assignment if everything was removed.
            const nextDepartment = nextParts.length > 0 ? nextParts.join(", ") : "General";
            return { id: (row as any).id, department: nextDepartment };
          })
          .filter(Boolean) as Array<{ id: string; department: string }>;

        for (const update of updates) {
          await supabase.from("members").update({ department: update.department }).eq("id", update.id);
        }
      }

      // Clean ministry references from finance_records notes
      const { data: financeRows } = await supabase
        .from("finance_records")
        .select("id, notes")
        .eq("church_id", profile!.id)
        .not("notes", "is", null);

      if (financeRows?.length) {
        const updates = financeRows
          .filter((r) => r.notes && (r.notes.includes(id) || (orgName && r.notes.includes(orgName))))
          .map((r) => {
            const cleaned = (r.notes || "")
              .split("\n")
              .filter((line) => {
                const t = line.trim();
                if (t.startsWith("Ministry: ") || t.startsWith("Organization: ")) {
                  const name = t.replace(/^(Ministry|Organization): /, "").trim();
                  return name !== orgName;
                }
                if (t.startsWith("Ministry IDs: ") || t.startsWith("Organization IDs: ")) {
                  const ids = t.replace(/^(Ministry|Organization) IDs: /, "").split(">").map((s) => s.trim()).filter(Boolean);
                  return !ids.includes(id);
                }
                return true;
              })
              .join("\n")
              .trim() || null;
            return { id: r.id, notes: cleaned };
          });

        for (const u of updates) {
          await supabase.from("finance_records").update({ notes: u.notes }).eq("id", u.id);
        }
      }

      // Clean ministry references from events notes
      const { data: eventRows } = await supabase
        .from("events")
        .select("id, notes")
        .eq("church_id", profile!.id)
        .not("notes", "is", null);

      if (eventRows?.length) {
        const updates = eventRows
          .filter((r) => r.notes && (r.notes.includes(id) || (orgName && r.notes.includes(orgName))))
          .map((r) => {
            const cleaned = (r.notes || "")
              .split("\n")
              .filter((line) => {
                const t = line.trim();
                if (t.startsWith("Ministry: ") || t.startsWith("Organization: ")) {
                  const name = t.replace(/^(Ministry|Organization): /, "").trim();
                  return name !== orgName;
                }
                if (t.startsWith("Ministry IDs: ") || t.startsWith("Organization IDs: ")) {
                  const ids = t.replace(/^(Ministry|Organization) IDs: /, "").split(">").map((s) => s.trim()).filter(Boolean);
                  return !ids.includes(id);
                }
                return true;
              })
              .join("\n")
              .trim() || null;
            return { id: r.id, notes: cleaned };
          });

        for (const u of updates) {
          await supabase.from("events").update({ notes: u.notes }).eq("id", u.id);
        }
      }

      // Now delete the organization (FK cascades handle leaders, assignments, budgets)
      const { error } = await supabase.from("organizations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organizations_full"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organization_leaders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_upcoming_events"] });
      queryClient.invalidateQueries({ queryKey: ["finance_records"] });
      queryClient.invalidateQueries({ queryKey: ["members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_all_members"] });
      queryClient.invalidateQueries({ queryKey: ["user_ministry_assignments"] });
      
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const openNewDialog = () => {
    if (!requireSubscription()) return;
    setEditingMinistry(null);
    setFormName("");
    setFormDescription("");
    setDialogOpen(true);
  };

  const openEditDialog = (ministry: Ministry) => {
    if (!requireSubscription()) return;
    if (isBuiltInGeneral(ministry.name)) {
      toast.error("General is a built-in ministry and cannot be edited.");
      return;
    }
    setEditingMinistry(ministry);
    setFormName(ministry.name);
    setFormDescription(ministry.description || "");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingMinistry(null);
    setFormName("");
    setFormDescription("");
  };

  const handleSubmit = () => {
    const normalizedName = formName.trim().toLowerCase();
    if (!normalizedName) {
      toast.error("Ministry name is required");
      return;
    }
    if (normalizedName === "general" && (!editingMinistry || !isBuiltInGeneral(editingMinistry.name))) {
      toast.error("General is built-in and already available by default.");
      return;
    }
    if (editingMinistry) {
      updateMinistryMutation.mutate({ id: editingMinistry.id, name: formName, description: formDescription });
    } else {
      addMinistryMutation.mutate({ name: formName, description: formDescription });
    }
  };

  const handleDeleteMinistry = (ministry: Ministry) => {
    if (!requireSubscription()) return;
    if (isBuiltInGeneral(ministry.name)) {
      toast.error("General is a built-in ministry and cannot be deleted.");
      return;
    }
    setDeleteConfirmText("");
    setDeletingMinistry(ministry);
  };

  const confirmDeleteMinistry = () => {
    if (!deletingMinistry) return;
    deleteMinistryMutation.mutate(deletingMinistry.id);
    setDeletingMinistry(null);
    setDeleteConfirmText("");
  };

  const isSaving = addMinistryMutation.isPending || updateMinistryMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="inline-flex h-9 items-center rounded-none bg-muted p-1 gap-1">
          <button
            type="button"
            className="h-7 px-3 text-xs font-medium rounded-none inline-flex items-center bg-primary text-primary-foreground"
            aria-current="page"
          >
            Ministries
          </button>
        </div>
      </div>

      <TableFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search ministries..."
        searchFirstOnMobile
        mobileControlsFirst
        compactDesktopFilters
        leadingControls={
          <div className="flex items-center border border-border rounded-lg overflow-hidden">
            <button onClick={() => setViewMode("cards")} className={cn("p-2 transition-colors", viewMode === "cards" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")} title="Card view">
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode("list")} className={cn("p-2 transition-colors", viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")} title="List view">
              <List className="w-4 h-4" />
            </button>
          </div>
        }
        extraControls={
          isAdmin ? (
            <Button size="sm" className="h-9 w-9 p-0 shrink-0 sm:w-auto sm:px-3" onClick={openNewDialog}>
              {isPreview && <span className="mr-2"></span>}
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Ministry</span>
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <div className="stat-card text-center py-12 text-muted-foreground">Loading ministries...</div>
      ) : ministries.length === 0 ? (
        <div className="stat-card text-center py-12">
          <Church className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">No ministries yet. Create your first ministry!</p>
          {isAdmin && (
            <Button size="sm" variant="outline" className="mt-4" onClick={openNewDialog}>
              {isPreview && <span className="mr-2"></span>}
              <Plus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Create Ministry</span>
            </Button>
          )}
        </div>
      ) : filteredMinistries.length === 0 ? (
        <div className="stat-card text-center py-12 text-muted-foreground text-sm">
          No ministries match your search.
        </div>
      ) : viewMode === "list" ? (
        <div className="stat-card p-0 overflow-hidden divide-y divide-border">
          {filteredMinistries.map((ministry) => (
            <div key={ministry.id} className="flex items-center justify-between px-5 py-3 hover:bg-secondary/20 transition-colors cursor-pointer" onClick={() => navigate(`/ministries/${ministry.id}`)}>
              <div className="flex items-center gap-3 min-w-0">
                <Church className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-medium text-foreground text-sm truncate">{ministry.name}</p>
                    {isBuiltInGeneral(ministry.name) && (
                      <Badge variant="secondary" className="h-5 px-2 text-[10px] font-semibold uppercase tracking-wide">Built-in</Badge>
                    )}
                  </div>
                  {ministry.description && <p className="text-xs text-muted-foreground truncate">{ministry.description}</p>}
                </div>
              </div>
              {isAdmin && !isBuiltInGeneral(ministry.name) && (
                <div className="shrink-0 ml-4" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 rounded hover:bg-secondary transition-colors"><MoreHorizontal className="w-4 h-4 text-muted-foreground" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(ministry)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeleteMinistry(ministry)}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMinistries.map((ministry) => (
            <Card key={ministry.id} className="group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer" onClick={() => navigate(`/ministries/${ministry.id}`)}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <CardTitle className="text-sm font-semibold truncate">{ministry.name}</CardTitle>
                      {isBuiltInGeneral(ministry.name) && (
                        <Badge variant="secondary" className="h-5 px-2 text-[10px] font-semibold uppercase tracking-wide">Built-in</Badge>
                      )}
                    </div>
                  </div>
                  {isAdmin && !isBuiltInGeneral(ministry.name) && (
                    <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-secondary transition-colors">
                          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(ministry)}>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => handleDeleteMinistry(ministry)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                  )}
                </div>
              </CardHeader>
              {ministry.description && (
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground">{ministry.description}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMinistry ? "Edit Ministry" : "New Ministry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ministry name"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSubmit(); } }}
              autoFocus
            />
            <Textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Short description (optional)"
              rows={2}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeDialog} disabled={isSaving}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!formName.trim() || isSaving}>
                {isSaving ? "Saving..." : editingMinistry ? "Save Changes" : "Add Ministry"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Type-to-delete confirmation dialog */}
      <AlertDialog open={!!deletingMinistry} onOpenChange={(open) => { if (!open) { setDeletingMinistry(null); setDeleteConfirmText(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ministry</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground">
                This will permanently delete <strong>{deletingMinistry?.name}</strong> and all its associated data. This action cannot be undone.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-sm text-muted-foreground">
              Type <span className="font-semibold text-foreground">delete</span> to confirm:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type delete"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeletingMinistry(null); setDeleteConfirmText(""); }}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={deleteConfirmText.trim().toLowerCase() !== "delete" || deleteMinistryMutation.isPending}
              onClick={confirmDeleteMinistry}
            >
              {deleteMinistryMutation.isPending ? "Deleting..." : "Delete Ministry"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}